import * as THREE from 'three';
import { PlanogramObject } from "./PlanogramObject.js";
import { PlanogramPositionCap } from "./PlanogramPositionCap.js";
import { PlanogramRenderer } from "./PlanogramRenderer.js";
import { PlanogramRendererResources, PlanogramPositionAttributes, PlanogramObjectAttributes } from './Resources.js';
import { PlanogramContainer } from './PlanogramContainer.js';
export class PlanogramPosition extends PlanogramObject{

    //#region Attributes
    static PositionAttributes = PlanogramPositionAttributes.PositionAttributes;

    static get observedAttributes(){
        return [
            ...Object.values(PlanogramObjectAttributes.Attributes),
            ...Object.values(PlanogramPosition.PositionAttributes)
        ];
    }
    //#endregion

    //#region Attribute Properties
    get productId() { return this.getAttribute(PlanogramPosition.PositionAttributes.ProductId); }
    get colour() { return  PlanogramObject.computeColour(this.getAttribute(PlanogramPosition.PositionAttributes.Colour), 0xffffff); }
    get isWireFrame () { return PlanogramRenderer.get(this).wireframePositions; }
    //#endregion

    //#region Private Properties
    /**@type {THREE.BufferGeometry|null} */
    #geometry = null;
    /**@type {THREE.Mesh|THREE.InstancedMesh|null} */
    #mesh = null;
    #firstRender = true; 
    #container;
    //#endregion

    //#region Properties
    get groupBoundingBox() {
        if(this.#mesh && this.#mesh.isInstancedMesh){
            this.#mesh.computeBoundingBox();
            return this.#mesh.boundingBox;
        }
        var box = new THREE.Box3().setFromObject(this.#mesh).getSize(new THREE.Vector3());
        return new THREE.Box3().setFromCenterAndSize(this.#mesh.position, box);
    }
    /**@type {PlanogramPositionCap[]} */
    get caps() { return [...this.children].filter(x=>x instanceof PlanogramPositionCap); }
    //#endregion

    //#region Methods
    initialise(){
        this.#generateGeometry();
        this.#generateMesh();
        this.#container = PlanogramContainer.get(this);
    }

    capAvailable(){ if(this.isConnected) this.#generateMesh(); }
    capUpdated(){ if(this.isConnected) this.#generateMesh(); }
    capRemoved(){ if(this.isConnected)  this.#generateMesh(); }
    transparencyChanged(){ this.#setColour(); }
    imagePlug(){
        if(this.isConnected){
            this.#setImageAttributes();
        }
    }

    #generateGeometry( reGeometry = false ) {
        if ( reGeometry && this.#geometry ) {
            this.#geometry.dispose();
            this.#geometry = null;
        }

        if(reGeometry || !this.#geometry)
            this.#geometry = new THREE.BoxGeometry(1, 1, 1);
        this.#geometry.computeBoundingBox();
        this.#geometry.center();
    }
    #generateMesh( reMesh = false ) {
        
        var totalMeshcount = this.caps.reduce((a,c)=> a + c.meshCount, 0);
        const renderer = PlanogramRenderer.get(this);
        if(!renderer?.resources)return;
        const shader = renderer.resources.UnitShader;

        if(!this.#mesh || !this.#mesh.isInstancedMesh || this.#mesh.count !== totalMeshcount){
            if (this.#mesh){
                this.#mesh.removeFromParent();
                this.#mesh.dispose();
            }
            this.#mesh = new THREE.InstancedMesh( this.#geometry, shader, totalMeshcount );
            this.addMesh(this.#mesh);
        }
        var dimensions = this.#geometry.boundingBox.getSize(new THREE.Vector3());

        this.caps.reduce((a,cap) => {
            cap.getVectors().forEach((position) => {
                this.#mesh.setMatrixAt( a++, new THREE.Matrix4().makeTranslation( cap.x + position.x, cap.y + position.y, cap.z + position.z )
                    .multiply(new THREE.Matrix4().makeRotationFromEuler(new THREE.Euler(cap.rotationX, cap.rotationY, cap.rotationZ))) 
                    .multiply(new THREE.Matrix4().makeScale( 
                        cap.width * ( cap.facingSpaceX === 0 && !cap.isTray ? cap.wide : 1), 
                        cap.height * ( cap.facingSpaceY === 0 && !cap.isTray? cap.high : 1),  
                        cap.depth * ( cap.facingSpaceZ === 0 && !cap.isTray? cap.deep : 1))));
            });
            return a;
        },0);

        var sizes = this.caps.flatMap(cap => Array( cap.meshCount ).fill([ cap.sizeX, cap.sizeY, cap.sizeZ ])).flat();
        var facings = this.caps.flatMap(cap=>Array( cap.meshCount ).fill([ cap.facingsX, cap.facingsY, cap.facingsZ ])).flat();
        var trayDimensions = this.caps.flatMap(cap=>Array( cap.meshCount ).fill([ cap.trayWidth, cap.trayHeight, cap.trayDepth ])).flat();
        this.#mesh.geometry.setAttribute("size", new THREE.InstancedBufferAttribute(new Float32Array( sizes ),3));
        this.#mesh.geometry.setAttribute("facings", new THREE.InstancedBufferAttribute(new Float32Array( facings ),3));
        this.#mesh.geometry.setAttribute("trayDimensions", new THREE.InstancedBufferAttribute(new Float32Array( trayDimensions ),3));
        this.#setColour();
        this.#setOptions();
        this.#mesh.instanceMatrix.needsUpdate = true;
        if(this.images.length>0)this.#setImageAttributes();
        this.setDecoratorGeometrySize();
        
    }
    

    #setColour(){
        var opacity = PlanogramRenderer.get(this)?.positionTransparency ?? 1;
        const container = PlanogramContainer.get(this);

        if(container?.showPositionsThroughBlocking){
            this.#mesh.renderOrder = 20;
            // this.#mesh.material.depthTest = true;
            // this.#mesh.material.depthWrite = true;
        }else{
            this.#mesh.renderOrder = 3;
            // this.#mesh.material.depthTest = true;
            // this.#mesh.material.depthWrite = true;
        }
        var colours = this.caps.flatMap(cap=>{
            return cap.isTray ? 
                Array( cap.meshCount/2 ).fill([ this.colour, 1, opacity, this.colour, cap.getTrayValue(), opacity ]):
                Array( cap.meshCount ).fill([ this.colour, 0, opacity ]);
        }).flat();
        this.#mesh.geometry.setAttribute("fillOptions", new THREE.InstancedBufferAttribute(new Float32Array( colours ),3));
    }

    #setImageAttributes(){

        const { rltArray, bfbkArray } = this.caps.reduce((a,c)=>{
            const { rltArray, bfbkArray } = c.getImageAttributes();
            a.rltArray.push(...rltArray);
            a.bfbkArray.push(...bfbkArray);
            return a;
        },{ rltArray:[], bfbkArray:[] });
        this.#mesh.geometry.setAttribute("rightLeftTop", new THREE.InstancedBufferAttribute(new Float32Array( rltArray ), 3 ));
        this.#mesh.geometry.setAttribute("bottomFrontBack", new THREE.InstancedBufferAttribute(new Float32Array( bfbkArray ), 3 ));
        this.#mesh.material.needsUpdate = true;
    }

    #setOptions(){
        var options = this.caps.flatMap(cap=>Array(cap.meshCount).fill([1, 0, this.isWireFrame ? 1 : 0])).flat();
        this.#mesh.geometry.setAttribute("options", new THREE.InstancedBufferAttribute(new Float32Array( options ),3));
    }
    
    //#endregion


    constructor() { super(); }

    //#region Life cycle
    connectedCallback() {
        this.initialise();
        this.#firstRender = false;
        super.connectedCallback();
        this.addMesh(this.#mesh);
        this.setDecoratorGeometrySize();
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (this.#firstRender || oldValue === newValue) return;
        super.attributeChangedCallback(name, oldValue, newValue);
        if( name === PlanogramPosition.PositionAttributes.Colour){
            this.#setColour();
        }
        if( [PlanogramObject.Attributes.LineColour,PlanogramObject.Attributes.LineThickness].includes(name) ){
            this.#setOptions();
        }
    }

    meshDispose(){
        this.#geometry.dispose();
        this.#geometry = null
        this.#mesh.dispose();
        this.#mesh = null;
    }

    dispose() {
        this.meshDispose();
        super.dispose();
    }
    //#endregion
}